/**
 * نقشه درختی (treemap)
 * =====================
 * فهرستی از چیزها با یک «وزن» می‌گیرد و مستطیل هرکدام را برمی‌گرداند، طوری
 * که مساحت هر مستطیل با وزنش متناسب باشد و کل فضا هم پر شود.
 *
 * روش: تقسیم دودویی. فهرستِ مرتب‌شده به دو نیمه هم‌وزن بریده می‌شود و
 * مستطیل از سمتِ **بلندترش** به همان نسبت نصف می‌شود. برش از ضلع بلند، همان
 * چیزی است که کاشی‌ها را نزدیک به مربع نگه می‌دارد؛ اگر همیشه از یک سمت
 * می‌بریدیم، به نوارهای باریکِ ناخوانا می‌رسیدیم.
 *
 * اینجا عمداً از هیچ کتابخانه‌ای استفاده نشده و هیچ وابستگی به مرورگر ندارد،
 * تا بشود مستقیم آزمونش کرد.
 */
export type Box = { x: number; y: number; w: number; h: number }
export type Weighted = { v: number }

/**
 * @param items باید بر اساس `v` نزولی مرتب باشد (خودِ تابع هم مرتب می‌کند)
 * @param w عرض کل فضا (پیش‌فرض ۱۰۰ ⇒ خروجی بر حسب درصد)
 * @param h ارتفاع کل فضا
 */
export function treemap<T extends Weighted>(items: T[], w = 100, h = 100): (T & Box)[] {
  const rows = items.filter((i) => Number.isFinite(i.v) && i.v > 0)
                    .sort((a, b) => b.v - a.v)
  const out: (T & Box)[] = []
  if (rows.length) split(rows, 0, 0, w, h, out)
  return out
}

function split<T extends Weighted>(
  items: T[], x: number, y: number, w: number, h: number, out: (T & Box)[],
) {
  if (!items.length || w <= 0 || h <= 0) return
  if (items.length === 1) { out.push({ ...items[0], x, y, w, h }); return }

  const total = items.reduce((s, i) => s + i.v, 0)
  if (!(total > 0)) return

  let acc = 0
  let k = 0
  while (k < items.length - 1 && acc + items[k].v <= total / 2) { acc += items[k].v; k++ }
  // هر دو نیمه باید دست‌کم یک عضو داشته باشند، وگرنه بازگشت بی‌پایان می‌شود
  if (k === 0) { k = 1; acc = items[0].v }

  // نسبت را کمی محدود می‌کنیم تا وقتی یک عضو غول‌پیکر است، بقیه به نوار
  // نامرئی تبدیل نشوند.
  const f = Math.min(Math.max(acc / total, 0.06), 0.94)
  const a = items.slice(0, k)
  const b = items.slice(k)
  if (w >= h) {
    split(a, x, y, w * f, h, out)
    split(b, x + w * f, y, w * (1 - f), h, out)
  } else {
    split(a, x, y, w, h * f, out)
    split(b, x, y + h * f, w, h * (1 - f), out)
  }
}
